import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  generateMigrationToken as generateTokenService,
  listMigrationTokens as listTokensService,
  revokeMigrationToken as revokeTokenService,
  listExternalMigrations as listMigrationsService,
  getExternalMigration as getMigrationService,
  retryExternalMigration as retryMigrationService,
  deleteExternalMigration as deleteMigrationService,
} from "../../services/ExternalMigration";

/**
 * Generate a migration token
 */
export const generateMigrationToken = createAsyncThunk(
  "externalMigration/generateToken",
  async (data, { rejectWithValue }) => {
    try {
      const response = await generateTokenService(data);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to generate token",
      );
    }
  },
);

/**
 * List migration tokens
 */
export const listMigrationTokens = createAsyncThunk(
  "externalMigration/listTokens",
  async (_, { rejectWithValue }) => {
    try {
      const response = await listTokensService();
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to list tokens",
      );
    }
  },
);

/**
 * Revoke a migration token
 */
export const revokeMigrationToken = createAsyncThunk(
  "externalMigration/revokeToken",
  async (tokenId, { rejectWithValue }) => {
    try {
      await revokeTokenService(tokenId);
      return tokenId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to revoke token",
      );
    }
  },
);

/**
 * List external migrations
 */
export const listExternalMigrations = createAsyncThunk(
  "externalMigration/listMigrations",
  async (params, { rejectWithValue }) => {
    try {
      const response = await listMigrationsService(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to list migrations",
      );
    }
  },
);

/**
 * Get single migration details
 */
export const getExternalMigration = createAsyncThunk(
  "externalMigration/getMigration",
  async (migrationId, { rejectWithValue }) => {
    try {
      const response = await getMigrationService(migrationId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to get migration",
      );
    }
  },
);

/**
 * Retry failed migration
 */
export const retryExternalMigration = createAsyncThunk(
  "externalMigration/retryMigration",
  async (migrationId, { rejectWithValue }) => {
    try {
      const response = await retryMigrationService(migrationId);
      return { migrationId, ...response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Retry failed",
      );
    }
  },
);

/**
 * Delete a migration
 */
export const deleteExternalMigration = createAsyncThunk(
  "externalMigration/deleteMigration",
  async (migrationId, { rejectWithValue }) => {
    try {
      await deleteMigrationService(migrationId);
      return migrationId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Delete failed",
      );
    }
  },
);

const externalMigrationSlice = createSlice({
  name: "externalMigration",
  initialState: {
    // Token generation
    generatedToken: null,
    generateLoading: false,
    generateError: null,

    // Token list
    tokens: [],
    tokensLoading: false,

    // Migrations list
    migrations: [],
    migrationsLoading: false,
    migrationsError: null,
    total: 0,

    // Single migration
    currentMigration: null,
    migrationLoading: false,
  },
  reducers: {
    clearGeneratedToken: (state) => {
      state.generatedToken = null;
      state.generateError = null;
    },
    updateMigrationProgress: (state, action) => {
      const { migrationId, status, step, progress, message } = action.payload;
      // Update in list
      const idx = state.migrations.findIndex((m) => m.id === migrationId);
      if (idx !== -1) {
        state.migrations[idx].status = status;
        state.migrations[idx]._progress = progress;
        state.migrations[idx]._step = step;
        state.migrations[idx]._message = message;
      }
      // Update current if viewing
      if (state.currentMigration?.id === migrationId) {
        state.currentMigration.status = status;
        state.currentMigration._progress = progress;
        state.currentMigration._step = step;
        state.currentMigration._message = message;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Generate token
      .addCase(generateMigrationToken.pending, (state) => {
        state.generateLoading = true;
        state.generateError = null;
      })
      .addCase(generateMigrationToken.fulfilled, (state, action) => {
        state.generateLoading = false;
        state.generatedToken = action.payload;
      })
      .addCase(generateMigrationToken.rejected, (state, action) => {
        state.generateLoading = false;
        state.generateError = action.payload;
      })

      // List tokens
      .addCase(listMigrationTokens.pending, (state) => {
        state.tokensLoading = true;
      })
      .addCase(listMigrationTokens.fulfilled, (state, action) => {
        state.tokensLoading = false;
        state.tokens = action.payload.tokens || [];
      })
      .addCase(listMigrationTokens.rejected, (state) => {
        state.tokensLoading = false;
      })

      // Revoke token
      .addCase(revokeMigrationToken.fulfilled, (state, action) => {
        state.tokens = state.tokens.map((t) =>
          t.id === action.payload ? { ...t, status: "REVOKED" } : t,
        );
      })

      // List migrations
      .addCase(listExternalMigrations.pending, (state) => {
        state.migrationsLoading = true;
        state.migrationsError = null;
      })
      .addCase(listExternalMigrations.fulfilled, (state, action) => {
        state.migrationsLoading = false;
        state.migrations = action.payload.migrations || [];
        state.total = action.payload.total || 0;
      })
      .addCase(listExternalMigrations.rejected, (state, action) => {
        state.migrationsLoading = false;
        state.migrationsError = action.payload;
      })

      // Get single migration
      .addCase(getExternalMigration.pending, (state) => {
        state.migrationLoading = true;
      })
      .addCase(getExternalMigration.fulfilled, (state, action) => {
        state.migrationLoading = false;
        state.currentMigration = action.payload.migration || null;
      })
      .addCase(getExternalMigration.rejected, (state) => {
        state.migrationLoading = false;
      })

      // Retry migration
      .addCase(retryExternalMigration.fulfilled, (state, action) => {
        const idx = state.migrations.findIndex(
          (m) => m.id === action.payload.migrationId,
        );
        if (idx !== -1) {
          state.migrations[idx].status = "ASSEMBLING";
        }
      })

      // Delete migration
      .addCase(deleteExternalMigration.fulfilled, (state, action) => {
        state.migrations = state.migrations.filter(
          (m) => m.id !== action.payload,
        );
      });
  },
});

export const { clearGeneratedToken, updateMigrationProgress } =
  externalMigrationSlice.actions;
export default externalMigrationSlice.reducer;
